import { useEffect, useRef, useState, type FC } from "react";
import { Trash2 } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/shared/components/ui/sheet";
import ConfirmDeleteDialog from "@/shared/components/overlay/ConfirmDeleteDialog";
import { DocumentBadge } from "@/shared/components/DocumentBadge";
import { useContactForm } from "@/shared/hooks/useContactForm";
import { useContactPhotoUrl } from "@/shared/hooks/useContactPhotoUrl";
import { useDocumentSheet } from "@/shared/hooks/useDocumentSheet";
import { useEstateContactMutations } from "../hooks/useEstateContactMutations";
import { saveProfilePicture } from "@/shared/utils/saveProfilePicture";
import { PersonContact } from "@/shared/types/estateContact";
import { buildAddress, parseAddress } from "@/shared/utils/addressUtils";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { splitName } from "@/shared/utils/names";

import ContactFormFields from "@/shared/components/Contact/ContactFormFields";
import { getRelationshipLabel, getDeceasedFirstName } from "@/shared/utils/contactLabels";
import { formatLegalRole } from "@/shared/utils/formatters";
import { LEGAL_ROLE_OPTIONS } from "@/shared/constants/constants";

interface LawyerEditContactSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  contact: PersonContact | null;
  estateId: number;
  deceasedUserData?: { displayName?: string; firstName?: string } | null;
  isPrimary?: boolean;
}

export const LawyerEditContactSheet: FC<LawyerEditContactSheetProps> = ({
  isOpen,
  onOpenChange,
  contact,
  estateId,
  deceasedUserData,
  isPrimary = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const { updateContactAsync, deleteContactAsync, isUpdating, isDeleting } =
    useEstateContactMutations(estateId);

  const { openDocument, documentSheet } = useDocumentSheet(estateId);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { photoUrl: contactPhotoUrl } = useContactPhotoUrl(
    contact?.profile_picture ? contact?.id || null : null,
  );

  const {
    formData,
    errors,
    setErrors,
    isSubmitting,
    setIsSubmitting,
    profilePictureUrl,
    selectedFile,
    validateForm,
    handleInputChange,
    handleAddressChange,
    handleProfilePictureSelect,
    uploadProfilePicture,
    resetForm,
    setFormData,
  } = useContactForm(estateId);

  useEffect(() => {
    if (isOpen && contact) {
      const fullName = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
      const legalRoleOptions = (contact.legal_role || []).map((role) => {
        const formattedLabel = formatLegalRole([role]);
        return (
          LEGAL_ROLE_OPTIONS.find((opt) => opt.label === formattedLabel) ?? {
            value: role,
            label: formattedLabel,
          }
        );
      });

      setFormData({
        fullName,
        firstName: contact.first_name || "",
        middleName: contact.middle_name || "",
        lastName: contact.last_name || "",
        legalRole: legalRoleOptions,
        relationship: contact.relationship || "",
        email: contact.email || "",
        phone: contact.phone || "",
        address: parseAddress(contact.address || ""),
        inviteToElayne: false,
      });
    } else if (!isOpen) {
      resetForm();
    }
  }, [isOpen, contact, setFormData, resetForm]);

  const handleSave = async () => {
    if (!contact) return;
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const { firstName, surName } = splitName(formData.fullName);
      const legalRoleValues = formData.legalRole.map((role) => role.value);

      let finalProfilePictureUrl = contact.profile_picture;
      if (selectedFile) {
        try {
          const uploadedUrl = await uploadProfilePicture(selectedFile);
          if (uploadedUrl) finalProfilePictureUrl = uploadedUrl;
        } catch (error) {
          console.error("Profile picture upload failed; saving update without new picture", error);
        }
      }

      const addressString = buildAddress(formData.address);

      const updatedContact = await updateContactAsync({
        id: contact.id,
        first_name: firstName,
        last_name: surName,
        email: formData.email || null,
        phone: formData.phone || null,
        address: addressString || null,
        relationship: formData.relationship || null,
        legal_role: legalRoleValues,
        profile_picture: finalProfilePictureUrl,
      });

      void resolveAddressLink({
        raw: addressString,
        purpose: "mailing",
        entity: { contact_id: contact.id },
      });

      if (selectedFile && finalProfilePictureUrl && updatedContact) {
        await saveProfilePicture({
          fileKey: finalProfilePictureUrl,
          contactId: updatedContact.id,
          userId: updatedContact.user_id,
        });
      }

      onOpenChange(false);
    } catch (error) {
      if (error instanceof Error && error.message.includes("email already exists")) {
        setErrors({ email: "A contact with this email already exists" });
      } else {
        console.error("Error updating contact:", error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!contact) return;
    setDeleteError(null);
    try {
      await deleteContactAsync(contact.id);
      setIsDeleteOpen(false);
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to delete contact:", error);
      setDeleteError("Failed to delete contact. Please try again.");
    }
  };

  if (!contact) return null;

  const contactName = [contact.first_name, contact.last_name].filter(Boolean).join(" ");
  const deceasedFirstName = getDeceasedFirstName(deceasedUserData);
  const relationshipLabel = getRelationshipLabel(formData.fullName, deceasedUserData, false);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onOpenChange}>
        <SheetContent
          ref={sheetRef}
          side="right"
          className="w-full sm:w-1/2 sm:max-w-[50vw] p-0 overflow-y-auto bg-white"
        >
          <SheetHeader className="flex flex-col bg-transparent border-b-0 px-[16px] sm:px-[32px] pt-[24px] pb-[16px]">
            <div className="flex items-center gap-3 pr-10">
              <button
                type="button"
                onClick={() => !isPrimary && fileInputRef.current?.click()}
                disabled={isPrimary}
                className="relative size-12 rounded-full bg-brand flex items-center justify-center text-white font-semibold overflow-hidden hover:opacity-90 transition-opacity shrink-0 disabled:hover:opacity-100 disabled:cursor-default"
                aria-label={isPrimary ? "Profile picture" : "Change profile picture"}
              >
                {profilePictureUrl || contactPhotoUrl ? (
                  <img
                    src={profilePictureUrl || contactPhotoUrl || ""}
                    alt={formData.fullName || "Profile"}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-base">
                    {formData.fullName
                      .split(" ")
                      .map((n) => n[0])
                      .filter(Boolean)
                      .slice(0, 2)
                      .join("")
                      .toUpperCase() || "?"}
                  </span>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfilePictureSelect}
              />
              <SheetTitle className="text-xl font-semibold leading-tight font-sans">
                {contactName}
              </SheetTitle>
              {contact.extracted_from_document && contact.source_document && (
                <DocumentBadge
                  sourceDocument={contact.source_document}
                  variant="long"
                  onClick={() => openDocument(contact.source_document)}
                  color="stone"
                />
              )}
            </div>
          </SheetHeader>
          <div className={`px-[16px] sm:px-[32px] pt-0 ${isPrimary ? "pb-[24px]" : "pb-0"}`}>
            {isPrimary && (
              <div className="mt-2 rounded-md bg-stone-50 border border-stone-200 px-3 py-2 text-sm text-secondary">
                This is the account owner. Contact them directly to update their details.
              </div>
            )}
            <ContactFormFields
              formData={formData}
              errors={errors}
              onInputChange={handleInputChange}
              onAddressChange={handleAddressChange}
              relationshipLabel={relationshipLabel}
              deceasedFirstName={deceasedFirstName}
              isMeContact={false}
              portalContainer={sheetRef.current}
              legalRoleEditable
              readOnly={isPrimary}
            />
          </div>
          {!isPrimary && (
            <SheetFooter className="flex flex-row justify-between px-[16px] sm:px-[24px] py-[24px]">
              <Button
                type="button"
                variant="ghost"
                onClick={() => {
                  setDeleteError(null);
                  setIsDeleteOpen(true);
                }}
                disabled={isSubmitting || isUpdating || isDeleting}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                aria-label="Delete"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
              <Button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting || isUpdating}
                variant="brand"
                className="md:w-fit"
              >
                {isSubmitting || isUpdating ? "Updating..." : "Update"}
              </Button>
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {documentSheet}

      <ConfirmDeleteDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
        title={`Delete ${contact.first_name || "this contact"}`}
        description={deleteError ?? "This action is permanent and cannot be undone."}
        isLoading={isDeleting}
      />
    </>
  );
};
